import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../components-v2/Theme/theme';
import { backgroundAlt, borderColor, brandPrimary, destructive, grayIconFill, midGrey } from '../../../styles/colors';

export const styleSheet = StyleSheet.create({
	modalContent: {
		maxWidth: 600,
		width: '100%',
	},
	dialogBody: {
		maxHeight: '60vh',
		overflowY: 'auto',
		paddingRight: 10,
	},
	formSection: {
		marginBottom: 20,
	},
	label: {
		display: 'block',
		fontWeight: 600,
		marginBottom: 6,
		fontSize: 14,
	},
	input: {
		width: '100%',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.sm + 4}px`,
		fontSize: 14,
		border: `1px solid ${borderColor}`,
		borderRadius: 4,
		outline: 'none',
		boxSizing: 'border-box',
		':focus': {
			borderColor: brandPrimary,
		},
	},
	selectTriggerFull: {
		width: '100%',
	},
	selectTrigger: {
		width: '100%',
	},
	membersSection: {
		marginTop: 24,
	},
	membersSectionTitle: {
		fontWeight: 600,
		fontSize: 14,
		display: 'block',
		marginBottom: 12,
	},
	addMemberButton: {
		color: brandPrimary,
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		fontSize: 14,
		padding: 0,
		':hover': {
			textDecoration: 'underline',
		},
	},
	membersList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
	},
	memberRow: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 12,
		padding: 12,
		border: `1px solid ${borderColor}`,
		borderRadius: 4,
		backgroundColor: backgroundAlt,
	},
	memberNumber: {
		fontWeight: 600,
		color: grayIconFill,
		minWidth: 20,
		paddingTop: 10,
	},
	memberFields: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	},
	memberFieldRow: {
		display: 'flex',
		gap: 8,
		alignItems: 'center',
	},
	memberFieldLabel: {
		fontSize: 12,
		color: midGrey,
		minWidth: 60,
	},
	memberFieldCombobox: {
		flex: 1,
	},
	memberFieldSelect: {
		flex: 1,
	},
	comboboxInput: {
		width: '100%',
	},
	priorityInput: {
		width: 60,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.sm + 2}px`,
		fontSize: 13,
		border: `1px solid ${borderColor}`,
		borderRadius: 4,
		outline: 'none',
		textAlign: 'center',
		':focus': {
			borderColor: brandPrimary,
		},
	},
	enabledCheckbox: {
		marginLeft: 8,
	},
	removeButton: {
		color: destructive,
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		fontSize: 18,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
		':hover': {
			backgroundColor: THEME.colors.hover.light,
			borderRadius: 4,
		},
	},
	emptyMembers: {
		color: midGrey,
		fontSize: 13,
		textAlign: 'center',
		padding: 20,
		border: `1px dashed ${borderColor}`,
		borderRadius: 4,
	},
	errorText: {
		color: destructive,
		fontSize: 12,
		marginTop: 4,
	},
	footerContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
	},
	footerButtons: {
		display: 'flex',
		gap: 12,
		justifyContent: 'flex-end',
	},
	categoriesDisplay: {
		color: grayIconFill,
		fontSize: 13,
		marginBottom: 16,
	},
	// Member selection checkbox
	memberSelectionCheckbox: {
		marginTop: 10,
		marginRight: 4,
	},
	// Header row with select all and edit settings
	membersHeaderRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 12,
	},
	selectionControls: {
		display: 'flex',
		alignItems: 'center',
		gap: 16,
	},
	selectAllLabel: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		fontSize: 13,
		cursor: 'pointer',
	},
	editSettingsLink: {
		color: brandPrimary,
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		fontSize: 13,
		padding: 0,
		':hover': {
			textDecoration: 'underline',
		},
	},
	editSettingsLinkDisabled: {
		color: grayIconFill,
		cursor: 'not-allowed',
		':hover': {
			textDecoration: 'none',
		},
	},
	// Bulk settings panel
	settingsPanel: {
		backgroundColor: backgroundAlt,
		border: `1px solid ${borderColor}`,
		borderRadius: 4,
		padding: 16,
		marginBottom: 16,
	},
	settingsPanelHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 16,
	},
	settingsPanelTitle: {
		fontSize: 14,
		fontWeight: 600,
		margin: 0,
	},
	settingsPanelClose: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		fontSize: 18,
		color: grayIconFill,
		padding: 4,
		':hover': {
			color: midGrey,
		},
	},
	settingRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		marginBottom: 12,
	},
	settingLabel: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		minWidth: 150,
		fontSize: 13,
	},
	settingControl: {
		flex: 1,
		maxWidth: 250,
	},
	settingControlWide: {
		flex: 1,
		maxWidth: 400,
	},
	settingControlDisabled: {
		opacity: 0.5,
		pointerEvents: 'none' as const,
	},
	applyButton: {
		marginTop: 16,
	},
	slotStartOptions: {
		display: 'flex',
		gap: 12,
		flexWrap: 'wrap' as const,
	},
	slotStartOption: {
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		fontSize: 13,
	},
	limitInputRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	limitInput: {
		width: 60,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
		fontSize: 13,
		border: `1px solid ${borderColor}`,
		borderRadius: 4,
		outline: 'none',
		textAlign: 'center' as const,
		':focus': {
			borderColor: brandPrimary,
		},
	},
});
